<template>
  <div class="dashboard-overview">
    <div class="welcome-section mb-8 px-4">
      <h1 class="text-h4 font-weight-bold primary--text mb-2">QUẢN LÝ LƯƠNG & GIỜ CÔNG</h1>
      <p class="text-subtitle-1 grey--text text--darken-1">Chào mừng bạn trở lại! Dưới đây là các phân hệ phổ biến giúp bạn quản lý hiệu quả hơn.</p>
    </div>

    <v-row class="px-2">
      <v-col v-for="(module, index) in popularModules" :key="index" cols="12" sm="6" md="4" lg="3">
        <v-hover v-slot="{ hover }">
          <v-card
            :to="module.route"
            :elevation="hover ? 12 : 2"
            :class="{ 'on-hover': hover }"
            class="module-card rounded-xl overflow-hidden transition-swing"
            height="100%"
          >
            <div class="card-gradient" :style="{ background: module.gradient }"></div>
            <v-card-text class="d-flex flex-column align-center text-center py-6">
              <v-avatar size="80" class="mb-4 elevation-4 white">
                <v-icon size="40" :color="module.iconColor">{{ module.icon }}</v-icon>
              </v-avatar>
              <h3 class="text-h6 font-weight-black mb-2 text-uppercase" :style="{ color: module.iconColor }">
                {{ module.title }}
              </h3>
              <p class="text-body-2 grey--text text--darken-2 mb-0 px-2">
                {{ module.description }}
              </p>
            </v-card-text>
            <v-divider class="mx-4"></v-divider>
            <v-card-actions class="justify-center py-3">
              <v-btn text small :color="module.iconColor" class="font-weight-bold">
                Truy cập ngay <v-icon small right>mdi-arrow-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-hover>
      </v-col>
    </v-row>

    <div class="mt-12 px-4 pb-8">
      <h2 class="text-h5 font-weight-bold mb-6 d-flex align-center">
        <v-icon left color="orange darken-2">mdi-chart-box-outline</v-icon>
        Tổng quan nhanh
      </h2>
      <v-row>
        <v-col cols="12" md="8">
          <v-card class="rounded-xl elevation-2 pa-4" min-height="350">
            <v-card-title class="font-weight-bold pb-2">
              <v-icon left color="red darken-1">mdi-clock-alert-outline</v-icon>
              Thống kê đi trễ trong tháng
            </v-card-title>
            <v-card-text>
              <chart 
                chartType="bar" 
                :chartData="lateChartData" 
                :chartOptions="lateChartOptions" 
                indexAxis="y"
                height="280px"
                @click:element="handleChartClick"
              ></chart>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card class="rounded-xl elevation-2 overflow-hidden" height="100%">
            <v-toolbar flat color="primary" dark>
              <v-toolbar-title class="subtitle-1">Thông báo mới nhất</v-toolbar-title>
            </v-toolbar>
            <v-list three-line>
              <v-list-item v-for="(n, i) in notifications" :key="i">
                <v-list-item-avatar :color="n.color" class="white--text">
                  <v-icon small color="white">{{ n.icon }}</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold">{{ n.title }}</v-list-item-title>
                  <v-list-item-subtitle>{{ n.time }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <v-dialog v-model="lateDetailDialog" max-width="800px" scrollable>
      <v-card class="rounded-xl overflow-hidden">
        <v-toolbar flat color="error" dark>
          <v-toolbar-title class="font-weight-bold">
            Chi tiết vi phạm: {{ selectedLateLabel }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <vue-excel-xlsx
            v-if="lateDetailItems && lateDetailItems.length > 0"
            :data="lateDetailItems"
            :columns="[
              { label: 'Mã nhân viên', field: 'employeeCode' },
              { label: 'Họ tên', field: 'employeeName' },
              { label: 'Số lần vi phạm', field: 'enrollmentCount' }
            ]"
            :file-name="'Chi_tiet_vi_pham_di_tre'"
            :file-type="'xlsx'"
            :sheet-name="'Chi tiết'"
          >
            <v-btn color="white" text small class="mr-2">
              <v-icon left small>mdi-microsoft-excel</v-icon>
              Tải Excel
            </v-btn>
          </vue-excel-xlsx>
          <v-btn icon @click="lateDetailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-data-table
            :headers="lateDetailHeaders"
            :items="lateDetailItems"
            :loading="loadingDetail"
            class="elevation-0"
            no-data-text="Không có dữ liệu vi phạm"
          >
            <template v-slot:[`item.employeeCode`]="{ item }">
              <span class="font-weight-bold primary--text">{{ item.employeeCode }}</span>
            </template>
            <template v-slot:[`item.violationCount`]="{ item }">
              <v-chip color="red" text-color="white" small class="font-weight-bold">
                {{ item.violationCount }} lần
              </v-chip>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Chart from '@/components/Charts/Chart.vue';
import http from '@/components/services/http-common';

export default {
  name: 'SalaryHome',
  components: {
    Chart
  },
  data() {
    return {
      popularModules: [
        {
          title: 'Tính công tháng',
          icon: 'mdi-calendar-check',
          description: 'Tổng hợp và đối soát dữ liệu chấm công hàng tháng của nhân viên.',
          route: { name: 'MonthlyTimeSheet' },
          iconColor: '#3F51B5',
          gradient: 'linear-gradient(135deg, #E8EAF6 0%, #3F51B5 100%)'
        },
        {
          title: 'Duyệt tăng ca',
          icon: 'mdi-clock-fast',
          description: 'Xét duyệt các đơn làm thêm giờ và quản lý quỹ thời gian ngoài giờ.',
          route: { name: 'SalaryConfirmOvertime' },
          iconColor: '#F57C00',
          gradient: 'linear-gradient(135deg, #FFF3E0 0%, #F57C00 100%)'
        },
        {
          title: 'Duyệt công tác',
          icon: 'mdi-briefcase-check',
          description: 'Xét duyệt các yêu cầu đi công tác và quản lý lịch trình làm việc bên ngoài.',
          route: { name: 'SalaryConfirmBusiness' },
          iconColor: '#00796B',
          gradient: 'linear-gradient(135deg, #E0F2F1 0%, #00796B 100%)'
        },
        {
          title: 'Duyệt nghỉ phép',
          icon: 'mdi-calendar-remove-outline',
          description: 'Kiểm tra và phê duyệt các đơn xin nghỉ phép, nghỉ ốm hoặc nghỉ không lương.',
          route: { name: 'SalaryConfirmLeave' },
          iconColor: '#D81B60',
          gradient: 'linear-gradient(135deg, #FCE4EC 0%, #D81B60 100%)'
        },
        {
          title: 'Hỗ trợ duyệt công',
          icon: 'mdi-face-agent',
          description: 'Giải đáp thắc mắc và hỗ trợ xử lý các vấn đề liên quan đến dữ liệu chấm công.',
          route: { name: 'TimesheetSupport' },
          iconColor: '#0097A7',
          gradient: 'linear-gradient(135deg, #E0F7FA 0%, #0097A7 100%)'
        },
        {
          title: 'Hỗ trợ thông tin lương',
          icon: 'mdi-bank-transfer',
          description: 'Xử lý các khiếu nại và phản hồi của nhân viên về bảng lương hàng tháng.',
          route: { name: 'SalarySupport' },
          iconColor: '#673AB7',
          gradient: 'linear-gradient(135deg, #EDE7F6 0%, #673AB7 100%)'
        },
        {
          title: 'Chấm công',
          icon: 'mdi-fingerprint',
          description: 'Theo dõi chi tiết dữ liệu vào/ra và các điểm chấm công hàng ngày.',
          route: { name: 'Attendance' },
          iconColor: '#43A047',
          gradient: 'linear-gradient(135deg, #E8F5E9 0%, #43A047 100%)'
        },
        {
          title: 'Cài đặt hệ thống',
          icon: 'mdi-cog-outline',
          description: 'Cấu hình các tham số hệ thống, thiết lập quy tắc tính lương và quản lý danh mục.',
          route: { name: 'SalarySetting' },
          iconColor: '#607D8B',
          gradient: 'linear-gradient(135deg, #ECEFF1 0%, #607D8B 100%)'
        }
      ],
      notifications: [
        { title: '*Đang cập nhật*', time: '10 phút trước', icon: 'mdi-check-circle', color: 'success' },
        { title: '*Đang cập nhật*', time: '1 giờ trước', icon: 'mdi-alert-circle', color: 'orange' },
        { title: '*Đang cập nhật*', time: '1 ngày trước', icon: 'mdi-information', color: 'info' }
      ],
      lateChartData: {
        labels: ['(1-3 lần/ tháng)', '(3-5 lần/ tháng)', '(trên 5 lần/ tháng)'],
        datasets: [
          {
            label: 'Số lượng nhân viên',
            backgroundColor: [
              'rgba(76, 175, 80, 0.7)',
              'rgba(255, 152, 0, 0.7)',
              'rgba(244, 67, 54, 0.7)'
            ],
            borderColor: [
              '#4CAF50',
              '#FF9800',
              '#F44336'
            ],
            borderWidth: 1,
            data: [0, 0, 0]
          }
        ]
      },
      lateChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
          legend: {
            display: false
          },
          datalabels: {
            anchor: 'end',
            align: 'right',
            color: '#333',
            font: {
              weight: 'bold'
            }
          }
        },
        scales: {
          x: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Số lượng nhân viên'
            }
          }
        }
      },
      lateDetailDialog: false,
      loadingDetail: false,
      selectedLateLabel: '',
      lateDetailItems: [],
      lateDetailHeaders: [
        { text: 'Mã nhân viên', value: 'employeeCode', align: 'start' },
        { text: 'Họ tên', value: 'employeeName', align: 'start' },
        { text: 'Số lần vi phạm', value: 'enrollmentCount', align: 'center', width: '150px' }
      ]
    }
  },
  created() {
    this.fetchLateCheckinData();
  },
  methods: {
    async fetchLateCheckinData() {
      try {
        const response = await http.get('salaryOverviews/late-checkin');
        if (response.data && Array.isArray(response.data.data)) {
          this.lateChartData.datasets[0].data = response.data.data;
          this.lateChartData = { ...this.lateChartData };
        }
      } catch (error) {
        console.error('Error fetching late checkin data:', error);
      }
    },
    async fetchLateDetail(type) {
      try {
        this.loadingDetail = true;
        this.lateDetailDialog = true;
        const response = await http.get(`salaryOverviews/late-checkin/${type}`);
        this.lateDetailItems = response.data.data || [];
      } catch (error) {
        console.error('Error fetching detail data:', error);
      } finally {
        this.loadingDetail = false;
      }
    },
    handleChartClick(event) {
      const { index, label } = event;
      this.selectedLateLabel = label;
      
      const types = ['under3', '3to5', 'above5'];
      const selectedType = types[index];
      
      if (selectedType) {
        this.fetchLateDetail(selectedType);
      }
    }
  }
}
</script>

<style scoped>
.dashboard-overview {
  padding: 0;
  background-color: transparent;
}

.module-card {
  position: relative;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  border: 1px solid rgba(0, 0, 0, 0.05) !important;
}

.on-hover {
  transform: translateY(-8px);
}

.card-gradient {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6px;
  opacity: 0.8;
}

.welcome-section h1 {
  letter-spacing: -0.5px;
  line-height: 1.2;
}

.transition-swing {
  transition: all 0.2s ease-in-out;
}
</style>
